import {
  MessagePathRecord,
  PathParser,
  TopicRecord,
} from "@/shared/domain/topics";

import {
  FilterResult,
  MessagePathFilterResult,
  TopicFilterResult,
} from "../filterResults";

type FilterResults = FilterResult[];

export class TopicFilterEngine {
  #topicRecords: TopicRecord[];
  #messagePathRecords: MessagePathRecord[];
  #allFilterResults: FilterResults = [];

  constructor(topicRecords: TopicRecord[]) {
    this.#topicRecords = topicRecords;

    this.#messagePathRecords = topicRecords.reduce<MessagePathRecord[]>(
      (messagePaths, topic) => {
        return messagePaths.concat(...topic.message_paths);
      },
      [],
    );

    this.#allFilterResults = [
      ...this.#topicRecords.map(
        (topic): TopicFilterResult => ({
          type: "topic",
          topic,
        }),
      ),
      ...this.#messagePathRecords.map(
        (messagePath): MessagePathFilterResult => ({
          type: "messagePath",
          messagePath,
        }),
      ),
    ];
  }

  filterByKeyword(term: string): FilterResults {
    if (!term) {
      return this.#allFilterResults;
    }

    const lowerTerm = term.toLowerCase();

    const topicMatches = [];
    for (const topic of this.#topicRecords) {
      // Preprocess the topicName
      const topicName = topic.topic_name.toLowerCase();

      // Check if there is an index of the term in the topicName
      const startIndex = topicName.indexOf(lowerTerm);

      if (startIndex === -1) {
        continue;
      }

      // Found a match, add it to the results
      const topicMatch: TopicFilterResult = {
        type: "topic",
        topic,
        match: {
          filter: { type: "keyword", term },
          position: [startIndex, startIndex + lowerTerm.length],
        },
      };
      topicMatches.push(topicMatch);
    }

    const messagePathMatches = [];
    for (const messagePath of this.#messagePathRecords) {
      // Only process the last part of the message path because nested message paths
      // contain the full path (e.g. "foo.bar" and "foo.bar.baz")
      const path =
        PathParser.splitMessagePath(messagePath.message_path)
          .at(-1)
          ?.toLowerCase() ?? "";

      const startIndex = path.indexOf(lowerTerm);
      if (startIndex !== -1) {
        // Found a match, add it to the results
        const messagePathMatch: MessagePathFilterResult = {
          type: "messagePath",
          messagePath,
          match: {
            filter: { type: "keyword", term },
            position: [startIndex, startIndex + lowerTerm.length],
          },
        };
        messagePathMatches.push(messagePathMatch);
      }
    }

    return [...topicMatches, ...messagePathMatches];
  }
}
